import styled from 'styled-components';
import { alpha } from '../../themes/brand';

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

export const ModalContainer = styled.div`
  background: ${({ theme }) => theme.card};
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 900px;
  width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.card};
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.textSecondary};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text};
  }
`;

export const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px;
`;

export const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

export const StepItem = styled.div<{ active?: boolean; completed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${({ theme, active, completed }) => {
    if (active) return theme.errorBackground;
    if (completed) return theme.successBackground;
    return theme.background;
  }};
  min-width: 200px;
  flex: 1;
`;

export const StepBullet = styled.span<{ active?: boolean; completed?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: ${({ theme, active, completed }) => {
    if (completed) return theme.primaryOn;
    if (active) return theme.primaryOn;
    return theme.textSecondary;
  }};
  background: ${({ theme, active, completed }) => {
    if (completed) return theme.success;
    if (active) return theme.error;
    return theme.background;
  }};
`;

export const StepLabel = styled.span<{ active?: boolean }>`
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme, active }) => (active ? theme.error : theme.text)};
`;

export const StepSubLabel = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
`;

export const StepDivider = styled.span<{ completed?: boolean }>`
  width: 32px;
  height: 2px;
  background: ${({ theme, completed }) => (completed ? theme.success : theme.background)};
  flex-shrink: 0;
`;

export const Section = styled.div`
  margin-bottom: 32px;
`;

export const SectionTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

export const SectionDescription = styled.p`
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.textSecondary};
  line-height: 1.6;
  font-size: 14px;
`;

export const SelectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

export const SelectionCount = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

export const HelperText = styled.span<{ accent?: boolean }>`
  display: block;
  font-size: 13px;
  color: ${props => (props.accent ? props.theme.success : props.theme.textSecondary)};
`;

export const SelectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SearchContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  padding: 8px 14px;
  border-radius: 999px;
  min-width: 220px;
`;

export const SearchIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.placeholderColor};
`;

export const SearchInput = styled.input`
  border: none;
  background: transparent;
  font-size: 14px;
  flex: 1;
  color: ${({ theme }) => theme.text};

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.placeholderColor};
  }
`;

export const ClearSearchButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.error};
  }
`;

export const QuickRepliesContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

export const QuickReply = styled.button<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: 2px solid ${props => props.selected ? props.theme.error : props.theme.border};
  background: ${props => props.selected ? props.theme.error : props.theme.card};
  color: ${props => props.selected ? props.theme.errorOn : props.theme.text};
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.error};
    background: ${props => props.selected ? props.theme.error : props.theme.errorBackground};
    color: ${props => props.selected ? props.theme.errorOn : props.theme.error};
  }
`;

export const SelectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
`;

export const SelectionCard = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  border: 2px solid ${props => props.selected ? props.theme.error : props.theme.border};
  border-radius: 12px;
  background: ${props => props.selected ? props.theme.errorBackground : props.theme.card};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.error};
    background: ${({ theme }) => theme.errorBackground};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${({ theme }) => alpha(theme.error, 0.1)};
  }
`;

export const CheckboxContainer = styled.div`
  flex-shrink: 0;
  margin-top: 2px;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    border: 2px solid ${({ theme }) => theme.border};
    border-radius: 4px;
    appearance: none;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;

    &:checked {
      background: ${({ theme }) => theme.error};
      border-color: ${({ theme }) => theme.error};

      &::after {
        content: '✓';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: ${({ theme }) => theme.errorOn};
        font-size: 12px;
        font-weight: bold;
      }
    }
  }
`;

export const CardIcon = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.background};
  border-radius: 8px;
  color: ${({ theme }) => theme.textSecondary};
`;

export const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const CardTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  word-break: break-word;
`;

export const CardDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.textSecondary};
  line-height: 1.5;
  word-break: break-word;
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  font-weight: 600;
  margin-top: 12px;
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
  padding: 24px 32px;
  border-top: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.card};
  justify-content: flex-end;
`;

export const ActionHint = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  flex: 1;
  min-width: 220px;
  margin-right: auto;
`;

export const CancelButton = styled.button`
  padding: 12px 24px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.card};
    border-color: ${({ theme }) => theme.placeholderColor};
  }
`;

export const ContinueButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  background: ${({ theme }) => theme.error};
  color: ${({ theme }) => theme.errorOn};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.error};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${({ theme }) => alpha(theme.error, 0.3)};
  }

  &:disabled {
    background: ${({ theme }) => theme.border};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 16px;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};

  h3 {
    margin: 16px 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
  }
`;

export const EmptyFilterState = styled(EmptyState)`
  border: 1px dashed ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.card};
  padding: 40px 32px;
`;

export const SelectAllButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${({ theme }) => theme.error};
  background: transparent;
  color: ${({ theme }) => theme.error};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;

  &:hover {
    background: ${({ theme }) => theme.error};
    color: ${({ theme }) => theme.errorOn};
  }
`;

export const SummarySection = styled.div`
  margin-bottom: 24px;
`;

export const SummaryTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

export const SummaryList = styled.ul`
  margin: 8px 0 0 0;
  padding-left: 20px;
  list-style-type: disc;
`;

export const SummaryListItem = styled.li`
  margin-bottom: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.text};
  line-height: 1.5;
`; 